// DictationStrip — the full-width "Microphone on" bar + viewport ring +
// browser-tab signal. Rendered via portal to document.body so it sits
// above every other z-index in the shell.
//
// Pure presentation: receives the dictation API and i18n, drives the UI.
// All state transitions are owned by useDictation().

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../i18n/index.js';
import styles from '../Coach.module.css';
import { setTabSignal } from './tabSignal.js';
import type { DictationApi } from './useDictation.js';

export interface DictationStripProps {
  d: DictationApi;
}

export function DictationStrip({ d }: DictationStripProps) {
  const { t } = useI18n();

  // Tab-title + favicon side effect. Keeps the signal in lockstep with
  // state transitions, including unmount (SPA route change away from the
  // composer while mic is paused — rare, but we clear it).
  useEffect(() => {
    if (d.state === 'live' || d.state === 'ending-soon') {
      setTabSignal('live', t('coach.dictation.tab.live'));
    } else if (d.state === 'paused') {
      setTabSignal('paused', t('coach.dictation.tab.paused'));
    } else {
      setTabSignal('off', '');
    }
    return () => setTabSignal('off', '');
  }, [d.state, t]);

  const showRing = d.state === 'live' || d.state === 'paused' || d.state === 'ending-soon';
  const showStrip =
    showRing || d.state === 'ended' || d.state === 'error';

  if (!showStrip) return null;

  const ringClass = [
    styles.dictRing,
    d.state === 'paused' ? styles.dictRingPaused : '',
    d.state === 'ending-soon' ? styles.dictRingPulse : '',
  ]
    .filter(Boolean)
    .join(' ');

  const stripClass = [
    styles.dictStrip,
    d.state === 'paused' ? styles.dictStripPaused : '',
    d.state === 'ended' ? styles.dictStripEnded : '',
    d.state === 'error' ? styles.dictStripError : '',
  ]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <>
      {showRing && <div className={ringClass} aria-hidden="true" />}
      <div className={stripClass} role="status" aria-live="polite">
        <span className={styles.dictStripDot} />
        <StripBody d={d} />
      </div>
    </>,
    document.body,
  );
}

function StripBody({ d }: { d: DictationApi }) {
  const { t, tt } = useI18n();

  if (d.state === 'live' || d.state === 'ending-soon') {
    const timer =
      d.state === 'ending-soon'
        ? tt('coach.dictation.strip.timeoutTimer', { time: formatCountdown(300 - d.elapsedSec) })
        : formatElapsed(d.elapsedSec);
    return (
      <>
        <span>
          <b>{t('coach.dictation.strip.liveTitle')}</b> — {t('coach.dictation.strip.liveBody')}
        </span>
        <span className={styles.dictStripTime}>{timer}</span>
        {d.detectedLang && (
          <span
            className={styles.dictStripLang}
            title={t('coach.dictation.strip.langTooltip')}
          >
            {d.detectedLang}
          </span>
        )}
        <span className={styles.dictStripFill} />
        {d.state === 'ending-soon' && (
          <span style={{ fontSize: '12.5px' }}>{t('coach.dictation.strip.timeoutBody')}</span>
        )}
        <button type="button" className={styles.dictStripBtn} onClick={d.pause}>
          {t('coach.dictation.strip.pause')}
        </button>
        <button type="button" className={styles.dictStripBtn} onClick={d.discard}>
          {t('coach.dictation.strip.discard')}
        </button>
        <button
          type="button"
          className={`${styles.dictStripBtn} ${styles.dictStripBtnSolid}`}
          onClick={d.stop}
        >
          {t('coach.dictation.strip.stop')}
        </button>
      </>
    );
  }

  if (d.state === 'paused') {
    return (
      <>
        <span>
          <b>{t('coach.dictation.strip.pausedTitle')}</b> — {t('coach.dictation.strip.pausedBody')}
        </span>
        <span className={styles.dictStripTime}>{formatElapsed(d.elapsedSec)}</span>
        <span className={styles.dictStripFill} />
        <button
          type="button"
          className={`${styles.dictStripBtn} ${styles.dictStripBtnSolid}`}
          onClick={() => void d.resume()}
        >
          {t('coach.dictation.strip.resume')}
        </button>
        <button type="button" className={styles.dictStripBtn} onClick={d.stop}>
          {t('coach.dictation.strip.stop')}
        </button>
      </>
    );
  }

  if (d.state === 'ended') {
    const timedOut = d.elapsedSec >= 300;
    return (
      <span>
        <b>{t('coach.dictation.strip.endedTitle')}</b> —{' '}
        {timedOut
          ? t('coach.dictation.strip.endedTimeout')
          : t('coach.dictation.strip.endedBody')}
      </span>
    );
  }

  if (d.state === 'error') {
    const body =
      d.errorKind === 'mic'
        ? t('coach.dictation.strip.errorMic')
        : t('coach.dictation.strip.errorDrop');
    return (
      <span>
        <b>{t('coach.dictation.strip.endedTitle')}</b> — {body}
      </span>
    );
  }

  return null;
}

function formatElapsed(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

function formatCountdown(s: number): string {
  const safe = Math.max(0, s);
  return formatElapsed(safe);
}
